import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, addDoc, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebaseconfig';
import { useAuth } from '../../contexts/AuthContext';
import { USER_ROLES } from '../../utils/roles';
import TransferModal from './TransferModal';
import './Savings.css';

const StaffSavingsDetail = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  const isAdmin = userRole === USER_ROLES.ADMIN;

  const [staffUser, setStaffUser] = useState(null);
  const [savings, setSavings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [amount, setAmount] = useState('');
  const [transactionType, setTransactionType] = useState('deposit');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [period, setPeriod] = useState('monthly'); // weekly | monthly | yearly | lastYear | all

  useEffect(() => {
    if (!isAdmin) {
      navigate('/savings', { replace: true });
      return;
    }
    const init = async () => {
      try {
        setLoading(true);
        const userDoc = await getDoc(doc(collection(db, 'users'), staffId));
        if (userDoc.exists()) {
          setStaffUser({ id: userDoc.id, ...userDoc.data() });
        }
        await fetchSavings(staffId);
      } catch (err) {
        console.error('Error loading staff detail:', err);
        setError('Failed to load staff savings detail');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [staffId, isAdmin]);

  const fetchSavings = async (userId) => {
    try {
      const savingsRef = collection(db, 'savings');
      const q = query(
        savingsRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const savingsList = querySnapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toDate() || new Date()
      }));
      setSavings(savingsList);
      setError('');
    } catch (err) {
      console.error('Error fetching savings:', err);
      setError('Failed to load savings data');
    }
  };

  const calculateBalance = (transactions) => {
    return transactions.reduce((total, transaction) => {
      if (transaction.type === 'deposit' || transaction.type === 'transfer_in') {
        return total + transaction.amount;
      } else if (transaction.type === 'withdrawal' || transaction.type === 'transfer_out') {
        return total - transaction.amount;
      }
      return total;
    }, 0);
  };

  const getPeriodStart = (mode) => {
    const now = new Date();
    const start = new Date(now);
    if (mode === 'weekly') {
      // Set to start of current week (Monday)
      const day = start.getDay(); // 0 Sun - 6 Sat
      const diffToMonday = (day === 0 ? -6 : 1) - day;
      start.setDate(start.getDate() + diffToMonday);
      start.setHours(0, 0, 0, 0);
      return start;
    }
    if (mode === 'monthly') {
      return new Date(start.getFullYear(), start.getMonth(), 1, 0, 0, 0, 0);
    }
    if (mode === 'yearly') {
      return new Date(start.getFullYear(), 0, 1, 0, 0, 0, 0);
    }
    if (mode === 'lastYear') {
      return new Date(start.getFullYear() - 1, 0, 1, 0, 0, 0, 0);
    }
    // For 'all', return a very old date to include all transactions
    if (mode === 'all') {
      return new Date(0); // January 1, 1970
    }
    return start;
  };

  const getPeriodEnd = (mode) => {
    const now = new Date();
    if (mode === 'lastYear') {
      return new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
    }
    // For other modes, return current date/time
    return now;
  };

  const filtered = savings.filter(t => {
    const transactionDate = new Date(t.createdAt);
    const periodStart = getPeriodStart(period);
    const periodEnd = getPeriodEnd(period);
    
    if (period === 'all') {
      return true; // Show all transactions
    }
    
    return transactionDate >= periodStart && transactionDate <= periodEnd;
  });
  const inflow = filtered
    .filter(t => t.type === 'deposit' || t.type === 'transfer_in')
    .reduce((sum, t) => sum + Number(t.amount || 0), 0);
  const outflow = filtered
    .filter(t => t.type === 'withdrawal' || t.type === 'transfer_out')
    .reduce((sum, t) => sum + Number(t.amount || 0), 0);
  const net = inflow - outflow;

  const getPeriodLabel = (mode) => {
    switch (mode) {
      case 'weekly':
        return 'This Week';
      case 'monthly':
        return 'This Month';
      case 'yearly':
        return 'This Year';
      case 'lastYear':
        return 'Last Year';
      case 'all':
        return 'All Time';
      default:
        return 'All Time';
    }
  };

  const formatAmount = (value) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(value);
  const formatDate = (date) => new Date(date).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const handleTransaction = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    try {
      const currentBalance = calculateBalance(savings);
      if (transactionType === 'withdrawal' && parseFloat(amount) > currentBalance) {
        setError('Insufficient balance for withdrawal');
        return;
      }
      const savingsRef = collection(db, 'savings');
      const newTransaction = {
        userId: staffId,
        amount: parseFloat(amount),
        type: transactionType,
        createdAt: Timestamp.now(),
        createdBy: currentUser.email,
        processedBy: currentUser.email
      };
      await addDoc(savingsRef, newTransaction);
      setSuccess(`${transactionType.charAt(0).toUpperCase() + transactionType.slice(1)} successful!`);
      setAmount('');
      await fetchSavings(staffId);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error processing transaction:', err);
      setError('Failed to process transaction');
    }
  };

  const handleTransfer = async (toStaff, transferAmount, description) => {
    try {
      if (!toStaff || parseFloat(transferAmount) <= 0) {
        setError('Invalid transfer details');
        return;
      }
      const currentBalance = calculateBalance(savings);
      if (parseFloat(transferAmount) > currentBalance) {
        setError('Insufficient balance for transfer');
        return;
      }
      const savingsRef = collection(db, 'savings');
      const timestamp = Timestamp.now();
      const withdrawalTransaction = {
        userId: staffId,
        amount: parseFloat(transferAmount),
        type: 'transfer_out',
        createdAt: timestamp,
        createdBy: currentUser.email,
        processedBy: currentUser.email,
        transferToUser: toStaff.id,
        transferToName: toStaff.displayName || toStaff.email,
        description
      };
      const depositTransaction = {
        userId: toStaff.id,
        amount: parseFloat(transferAmount),
        type: 'transfer_in',
        createdAt: timestamp,
        createdBy: currentUser.email,
        processedBy: currentUser.email,
        transferFromUser: staffId,
        transferFromName: staffUser?.displayName || staffUser?.email,
        description
      };
      await Promise.all([
        addDoc(savingsRef, withdrawalTransaction),
        addDoc(savingsRef, depositTransaction)
      ]);
      setSuccess('Transfer completed successfully!');
      setShowTransferModal(false);
      await fetchSavings(staffId);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error processing transfer:', err);
      setError('Failed to process transfer');
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="savings-container">
      <div className="savings-header">
        <div className="header-top">
          <h2>Staff Savings Detail</h2>
          <button className="add-staff-button" onClick={() => navigate('/savings')}>Back to Staff List</button>
        </div>
        <div className="period-filter">
          <label htmlFor="period">View</label>
          <select id="period" value={period} onChange={(e) => setPeriod(e.target.value)}>
            <option value="weekly">This Week</option>
            <option value="monthly">This Month</option>
            <option value="yearly">This Year</option>
            <option value="lastYear">Last Year</option>
            <option value="all">All Transactions</option>
          </select>
        </div>
        {staffUser && (
          <div className="staff-summary">
            <h3>{staffUser.displayName || 'Unknown'}</h3>
            <p>{staffUser.email}</p>
            <p className="staff-role">{staffUser.role}</p>
          </div>
        )}
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-title">Balance</div>
            <div className="summary-value">{formatAmount(calculateBalance(savings))}</div>
          </div>
          <div className="summary-card">
            <div className="summary-title">Inflow ({getPeriodLabel(period)})</div>
            <div className="summary-value positive">{formatAmount(inflow)}</div>
          </div>
          <div className="summary-card">
            <div className="summary-title">Outflow ({getPeriodLabel(period)})</div>
            <div className="summary-value negative">{formatAmount(outflow)}</div>
          </div>
          <div className="summary-card">
            <div className="summary-title">Net ({getPeriodLabel(period)})</div>
            <div className={`summary-value ${net >= 0 ? 'positive' : 'negative'}`}>{formatAmount(net)}</div>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="transaction-form">
        <h3>New Transaction</h3>
        <form onSubmit={handleTransaction}>
          <div className="form-group">
            <label htmlFor="amount">Amount (NGN)</label>
            <input
              type="number"
              id="amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="0.01"
              required
              placeholder="Enter amount"
            />
          </div>

          <div className="form-group">
            <label htmlFor="type">Transaction Type</label>
            <select
              id="type"
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
              required
            >
              <option value="deposit">Deposit</option>
              <option value="withdrawal">Withdrawal</option>
            </select>
          </div>

          <button type="submit" className="submit-button">Process Transaction</button>
          <button type="button" className="transfer-button" onClick={() => setShowTransferModal(true)}>Transfer</button>
        </form>
      </div>

      {filtered.length > 0 && (
        <div className="transactions-list">
          <h3>Transaction History ({getPeriodLabel(period)})</h3>
          <div className="transactions-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Description</th>
                  <th>Processed By</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{formatDate(transaction.createdAt)}</td>
                    <td className={`transaction-type ${transaction.type}`}>
                      {transaction.type}
                    </td>
                    <td className={`transaction-amount ${transaction.type}`}>
                      {formatAmount(transaction.amount)}
                    </td>
                    <td>{transaction.description || '-'}</td>
                    <td>{transaction.processedBy || transaction.createdBy}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showTransferModal && (
        <TransferModal
          onClose={() => setShowTransferModal(false)}
          onTransfer={handleTransfer}
          fromStaff={staffUser}
          currentBalance={calculateBalance(savings)}
        />
      )}
    </div>
  );
};

export default StaffSavingsDetail;


